import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import "./App.css";
import {
  Layout,
  Tour,
  TourProps,
  ConfigProvider,
  Drawer,
} from "antd";
import { Content, Header } from "antd/es/layout/layout";
import HeaderComponent from "./HeaderComponent";
import { useAuth0 } from "@auth0/auth0-react";
import { PermissionsProvider } from "./PermissionsProvider";
import LeftSiderComponent from "./LeftSiderComponent";
import RightSiderComponent from "./RightSiderComponent";
import { EvaluateResponse } from "./model_gen/evaluation";
import { Sentinel } from "./model_gen/sentinel";
import { SandboxMessage } from "./model_gen/sandbox";
import SyncChatComponent from "./SyncChatComponent";
import VoiceDemo from "./VoiceDemo";

declare global {
  interface Window {
    heap: any;
  }
}

interface AppLayoutProps {
  children: React.ReactNode;
  evalResponses: EvaluateResponse[];
  setEvalResponses: React.Dispatch<React.SetStateAction<EvaluateResponse[]>>;
  evalResponsesRef: React.MutableRefObject<EvaluateResponse[]>;
  considerTourStep: (step: number) => void;
  tripFault: (sentinel: Sentinel) => void;
  models: string[];
  setModels: (models: string[]) => void;
  temperature: number;
  setTemperature: (temperature: number) => void;
  evaluations: boolean;
  setEvaluations: (evaluations: boolean) => void;
  autocorrect: boolean;
  setAutocorrect: (autocorrect: boolean) => void;
  retrieveContext: boolean;
  setRetrieveContext: (retrieveContext: boolean) => void;
  streaming: boolean;
  setStreaming: (streaming: boolean) => void;
  targetMessage: SandboxMessage | null;
  setTargetMessage: React.Dispatch<React.SetStateAction<SandboxMessage | null>>;
  onStartNewConversation: (
    prompt: string,
    makeAssistantFirst: boolean,
    summaryMode: boolean,
    context: string,
    applicationName?: string
  ) => void;
  chatComponentRef: React.RefObject<any>;
  agentId: number | null;
  setAgentId: (agentId: number | null) => void;
  agentSettings: any | null;
  setAgentSettings: (settings: any | null) => void;
  modelCategory: string;
  onModelCategoryChange: (category: string) => void;
  conversation?: any;
  hasMessages?: boolean;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  evalResponses,
  setEvalResponses,
  evalResponsesRef,
  considerTourStep,
  tripFault,
  models,
  setModels,
  temperature,
  setTemperature,
  evaluations,
  setEvaluations,
  autocorrect,
  setAutocorrect,
  retrieveContext,
  setRetrieveContext,
  streaming,
  setStreaming,
  targetMessage,
  setTargetMessage,
  onStartNewConversation,
  chatComponentRef,
  agentId,
  setAgentId,
  agentSettings,
  setAgentSettings,
  modelCategory,
  onModelCategoryChange,
  conversation,
  hasMessages = false,
}) => {
  const [leftSiderVisible, setLeftSiderVisible] = useState(false);
  const [rightSiderVisible, setRightSiderVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track touch start positions
  const touchStartX = useRef(0);
  const touchThreshold = 50; // Minimum distance to trigger swipe

  // Function to open right sider - will be passed to SyncChatComponent
  const openRightSider = () => {
    if (isMobile) {
      setRightSiderVisible(true);
    }
  };

  // Handle refresh - clear messages and start new session
  const handleRefresh = () => {
    console.log("[AppLayout] Refreshing");
    console.log("chatComponentRef.current:", chatComponentRef.current);
    if (chatComponentRef.current?.clearConversation) {
      console.log("Calling clearConversation");
      chatComponentRef.current.clearConversation();
    } else {
      console.warn(
        "Cannot call clearConversation - ref or method not available"
      );
    }
  };

  // Handle window resize to detect mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      // On desktop, siders are always visible
      if (!mobile) {
        setLeftSiderVisible(false);
        setRightSiderVisible(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle touch events for swiping to reveal siders
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isMobile) return;

      const touchEndX = e.changedTouches[0].clientX;
      const screenWidth = window.innerWidth;
      const deltaX = touchEndX - touchStartX.current;

      // Right to left swipe near right edge (show right sider)
      if (touchStartX.current > screenWidth - 20 && deltaX < -touchThreshold) {
        setRightSiderVisible(true);
      }

      // Left to right swipe near left edge (show left sider)
      if (touchStartX.current < 20 && deltaX > touchThreshold) {
        setLeftSiderVisible(true);
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile]);

  // Handler for tapping screen edges
  const handleScreenEdgeTap = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isMobile) return;

    const clientX =
      "touches" in e
        ? (e as React.TouchEvent).touches[0].clientX
        : (e as React.MouseEvent).clientX;

    const screenWidth = window.innerWidth;

    if (clientX < 20) {
      setLeftSiderVisible(true);
    } else if (clientX > screenWidth - 20) {
      setRightSiderVisible(true);
    }
  };

  return (
    <Layout
      style={{ maxHeight: "calc(100vh)", height: "calc(100vh)" }}
      onClick={handleScreenEdgeTap}
      onTouchStart={handleScreenEdgeTap}>
      <Header className="Header">
        <HeaderComponent
          onRefresh={handleRefresh}
          onStartNewConversation={onStartNewConversation}
          modelCategory={modelCategory}
          conversation={conversation}
          hasMessages={hasMessages}
        />
      </Header>
      <Layout style={{ height: "100%" }}>
        {isMobile ? (
          // Mobile layout with drawers
          <>
            <Drawer
              title="Configuration & Sentinels"
              placement="left"
              closable={true}
              onClose={() => setLeftSiderVisible(false)}
              open={leftSiderVisible}
              width="100%"
              styles={{
                body: {
                  padding: 0,
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: "500px",
                },
                header: { padding: "12px 16px" },
                content: {
                  width: "100%",
                  overflow: "hidden",
                  maxWidth: "500px",
                },
                wrapper: { width: "100%", maxWidth: "500px" },
              }}>
              <LeftSiderComponent
                tripFault={tripFault}
                considerTourStep={considerTourStep}
                models={models}
                setModels={setModels}
                temperature={temperature}
                setTemperature={setTemperature}
                evaluations={evaluations}
                setEvaluations={setEvaluations}
                autocorrect={autocorrect}
                setAutocorrect={setAutocorrect}
                retrieveContext={retrieveContext}
                setRetrieveContext={setRetrieveContext}
                streaming={streaming}
                setStreaming={setStreaming}
                agentId={agentId}
                setAgentId={setAgentId}
                agentSettings={agentSettings}
                setAgentSettings={setAgentSettings}
                onModelCategoryChange={onModelCategoryChange}
              />
            </Drawer>

            <Drawer
              title="Completion Details"
              placement="right"
              closable={true}
              onClose={() => setRightSiderVisible(false)}
              open={rightSiderVisible}
              width="100%"
              styles={{
                body: {
                  padding: 0,
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: "500px",
                },
                header: { padding: "12px 16px" },
                content: {
                  width: "100%",
                  overflow: "hidden",
                  maxWidth: "500px",
                },
                wrapper: { width: "100%", maxWidth: "500px" },
              }}>
              <RightSiderComponent
                evalResponses={evalResponses}
                targetMessage={targetMessage}
              />
            </Drawer>

            {/* Edge indicators for mobile users */}
            <div className="edge-indicator-left" />
            <div className="edge-indicator-right" />
          </>
        ) : (
          // Desktop layout with static siders
          <>
            <LeftSiderComponent
              tripFault={tripFault}
              considerTourStep={considerTourStep}
              models={models}
              setModels={setModels}
              temperature={temperature}
              setTemperature={setTemperature}
              evaluations={evaluations}
              setEvaluations={setEvaluations}
              autocorrect={autocorrect}
              setAutocorrect={setAutocorrect}
              retrieveContext={retrieveContext}
              setRetrieveContext={setRetrieveContext}
              streaming={streaming}
              setStreaming={setStreaming}
              agentId={agentId}
              setAgentId={setAgentId}
              agentSettings={agentSettings}
              setAgentSettings={setAgentSettings}
              onModelCategoryChange={onModelCategoryChange}
            />
          </>
        )}

        <Content
          style={{
            background: "#fff",
            overflow: "hidden",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}>
          {React.cloneElement(children as React.ReactElement, {
            openRightSider,
          })}
        </Content>

        {!isMobile && (
          <RightSiderComponent
            evalResponses={evalResponses}
            targetMessage={targetMessage}
          />
        )}
      </Layout>
    </Layout>
  );
};

const App: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
  } = useAuth0();
  const domain = "dev-ociu84lf8keizm5i.us.auth0.com";
  const [userMeta, setUserMeta] = useState<any>([]);
  const [evalResponses, setEvalResponses] = useState<EvaluateResponse[]>([]);
  const evalResponsesRef = useRef<EvaluateResponse[]>([]);
  const chooseApplicationRef = useRef(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStepsShown, setTourStepsShown] = useState<number[]>([0]);
  const [models, setModels] = useState<string[]>(["openai:gpt-4o-mini"]);
  const [temperature, setTemperature] = useState<number>(0);
  const [evaluations, setEvaluations] = useState<boolean>(false);
  const [autocorrect, setAutocorrect] = useState<boolean>(false);
  const [retrieveContext, setRetrieveContext] = useState<boolean>(false);
  const [streaming, setStreaming] = useState<boolean>(true);
  const [targetMessage, setTargetMessage] = useState<SandboxMessage | null>(
    null
  );
  const chatComponentRef = useRef<any>(null);
  const [agentId, setAgentId] = useState<number | null>(null);
  const [agentSettings, setAgentSettings] = useState<any | null>(null);
  const [modelCategory, setModelCategory] = useState<string>("agents");
  const [systemPrompt, setSystemPrompt] = useState<string>("");
  const [assistantFirst, setAssistantFirst] = useState<boolean>(false);
  const [conversationSummaryMode, setConversationSummaryMode] =
    useState<boolean>(false);
  const [context, setContext] = useState<string>("");
  const [applicationName, setApplicationName] = useState<string>("");
  const [startConversationCounter, setStartConversationCounter] = useState<number>(0);
  const [conversation, setConversation] = useState<any>(null);
  const [hasMessages, setHasMessages] = useState<boolean>(false);

  const tourSteps: TourProps["steps"] = [
    {
      title: "Welcome to Maitai Demo",
      description: (
        <>
          <p>
            This demo shows how the Maitai platform can help you build better
            LLM-powered applications.
          </p>
          <p>
            To get started, click the "New Conversation" button to create a
            conversation with a system prompt.
          </p>
        </>
      ),
      target: () => chooseApplicationRef.current,
      nextButtonProps: {
        children: "Got it!",
        onClick: () => {
          setTourOpen(false);
        },
      },
    },
  ];

  const considerTourStep = (step: number) => {
    if (!tourStepsShown.includes(step)) {
      window.heap.track("Tour Shown - Welcome");
      setTourOpen(true);
      setTourStepsShown([...tourStepsShown, step]);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently({
        authorizationParams: {
          audience: `https://${domain}/api/v2/`,
          scope: "openid profile email read:current_user",
        },
      })
        .then((accessToken) => {
          if (!accessToken) {
            console.error("No access token returned");
            return;
          }
          // Use the access token to fetch user profile from the /userinfo endpoint
          fetch(`https://${domain}/api/v2/users/${user?.sub}`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          })
            .then((response) => response.json())
            .then((profile) => {
              // Access user_metadata from the profile
              window.heap.addUserProperties({ name: profile?.name });
              setUserMeta(profile["user_metadata"]);
              // You can now set this information in your state or use it as needed
            })
            .catch((err) => {
              console.error("Error fetching user profile", err);
              logout({
                logoutParams: { returnTo: window.location.origin },
              }).then(() => {
                loginWithRedirect();
              });
            });
        })
        .catch((err) => {
          console.error("Error getting access token", err);
          if (err.error === "consent_required") {
            logout({ logoutParams: { returnTo: window.location.origin } }).then(
              () => {
                loginWithRedirect();
              }
            );
          }
        });
    } else if (!isLoading) {
      loginWithRedirect();
    }
  }, [
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
  ]);

  useEffect(() => {
    if (!userMeta || !userMeta.api_key) {
      // logout({logoutParams: {returnTo: window.location.origin }}).then(() => {
      //     loginWithRedirect();
      // });
    }
  }, [userMeta, logout]);

  useEffect(() => {
    considerTourStep(0);
  }, []);

  const tripFault = (sentinel: Sentinel) => {
    console.log("tripping fault", sentinel);
    if (chatComponentRef.current) {
      chatComponentRef.current.sendFaultyMessage(sentinel);
    }
  };

  // Add handler for new conversations
  const handleStartNewConversation = (
    prompt: string,
    makeAssistantFirst: boolean,
    summaryMode: boolean,
    context: string,
    applicationName?: string
  ) => {
    console.log(
      "handleStartNewConversation",
      prompt,
      makeAssistantFirst,
      summaryMode,
      context,
      applicationName
    );
    setSystemPrompt(prompt);
    setAssistantFirst(makeAssistantFirst);
    setConversationSummaryMode(summaryMode);
    setContext(context);
    setApplicationName(applicationName || "");
    setStartConversationCounter((prev) => prev + 1);
  };

  // Define your custom theme
  const customTheme = {
    token: {
      colorPrimary: "#21b892",
      colorSecondary: "#ffa658",
      // Add more color customizations here
    },
  };

  return (
    <ConfigProvider theme={customTheme}>
      {userMeta && userMeta.api_key !== undefined ? (
        <PermissionsProvider apiKey={userMeta.api_key}>
          <Routes>
            <Route
              path="/voice_demo"
              element={
                <AppLayout
                  tripFault={tripFault}
                  evalResponses={evalResponses}
                  setEvalResponses={setEvalResponses}
                  evalResponsesRef={evalResponsesRef}
                  considerTourStep={considerTourStep}
                  models={models}
                  setModels={setModels}
                  temperature={temperature}
                  setTemperature={setTemperature}
                  evaluations={evaluations}
                  setEvaluations={setEvaluations}
                  autocorrect={autocorrect}
                  setAutocorrect={setAutocorrect}
                  retrieveContext={retrieveContext}
                  setRetrieveContext={setRetrieveContext}
                  streaming={streaming}
                  setStreaming={setStreaming}
                  targetMessage={targetMessage}
                  setTargetMessage={setTargetMessage}
                  onStartNewConversation={handleStartNewConversation}
                  chatComponentRef={chatComponentRef}
                  agentId={agentId}
                  setAgentId={setAgentId}
                  agentSettings={agentSettings}
                  setAgentSettings={setAgentSettings}
                  modelCategory={modelCategory}
                  onModelCategoryChange={setModelCategory}
                  conversation={conversation}
                  hasMessages={hasMessages}>
                  <VoiceDemo />
                </AppLayout>
              }
            />
            <Route
              path="*"
              element={
                <AppLayout
                  tripFault={tripFault}
                  evalResponses={evalResponses}
                  setEvalResponses={setEvalResponses}
                  evalResponsesRef={evalResponsesRef}
                  considerTourStep={considerTourStep}
                  models={models}
                  setModels={setModels}
                  temperature={temperature}
                  setTemperature={setTemperature}
                  evaluations={evaluations}
                  setEvaluations={setEvaluations}
                  autocorrect={autocorrect}
                  setAutocorrect={setAutocorrect}
                  retrieveContext={retrieveContext}
                  setRetrieveContext={setRetrieveContext}
                  streaming={streaming}
                  setStreaming={setStreaming}
                  targetMessage={targetMessage}
                  setTargetMessage={setTargetMessage}
                  onStartNewConversation={handleStartNewConversation}
                  chatComponentRef={chatComponentRef}
                  agentId={agentId}
                  setAgentId={setAgentId}
                  agentSettings={agentSettings}
                  setAgentSettings={setAgentSettings}
                  modelCategory={modelCategory}
                  onModelCategoryChange={setModelCategory}
                  conversation={conversation}
                  hasMessages={hasMessages}>
                  <SyncChatComponent
                    evalResponses={evalResponses}
                    setEvalResponses={setEvalResponses}
                    evalResponsesRef={evalResponsesRef}
                    ref={chatComponentRef}
                    models={models}
                    temperature={temperature}
                    evaluations={evaluations}
                    autocorrect={autocorrect}
                    retrieveContext={retrieveContext}
                    streaming={streaming}
                    targetMessage={targetMessage}
                    setTargetMessage={setTargetMessage}
                    systemPrompt={systemPrompt}
                    assistantFirst={assistantFirst}
                    conversationSummaryMode={conversationSummaryMode}
                    context={context}
                    agentId={agentId}
                    agentSettings={agentSettings}
                    startConversationCounter={startConversationCounter}
                    applicationName={applicationName}
                    onConversationChange={setConversation}
                    onHasMessagesChange={setHasMessages}
                  />
                </AppLayout>
              }
            />
          </Routes>
        </PermissionsProvider>
      ) : null}
      <Tour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        steps={tourSteps}
      />
    </ConfigProvider>
  );
};

export default () => (
  <Router>
    <App />
  </Router>
);
